import React from 'react';
import {
  ExclamationTriangleIcon,
  XCircleIcon,
  WifiIcon,
  ArrowPathIcon,
  EyeSlashIcon,
  DocumentIcon,
  UserIcon,
  ServerIcon
} from '@heroicons/react/24/outline';

interface ErrorStateProps {
  type?: 'network' | 'notFound' | 'permission' | 'server' | 'empty' | 'offline' | 'custom';
  title?: string;
  message?: string;
  actionLabel?: string;
  onAction?: () => void;
  showRetry?: boolean;
  onRetry?: () => void;
  className?: string;
  illustration?: React.ReactNode;
}

const ErrorState: React.FC<ErrorStateProps> = ({
  type = 'custom',
  title,
  message,
  actionLabel,
  onAction,
  showRetry = true,
  onRetry,
  className = '',
  illustration
}) => {
  const getErrorConfig = () => {
    switch (type) {
      case 'network':
        return {
          icon: WifiIcon,
          defaultTitle: 'Connection Error',
          defaultMessage: 'Unable to connect to the server. Please check your internet connection and try again.',
          iconColor: 'text-red-500',
          bgColor: 'bg-red-50'
        };
      case 'notFound':
        return {
          icon: EyeSlashIcon,
          defaultTitle: 'Not Found',
          defaultMessage: 'The requested resource could not be found. It may have been moved or deleted.',
          iconColor: 'text-gray-500',
          bgColor: 'bg-gray-50'
        };
      case 'permission':
        return {
          icon: XCircleIcon,
          defaultTitle: 'Access Denied',
          defaultMessage: 'You don\'t have permission to access this resource. Please contact your administrator.',
          iconColor: 'text-amber-500',
          bgColor: 'bg-amber-50'
        };
      case 'server':
        return {
          icon: ServerIcon,
          defaultTitle: 'Server Error',
          defaultMessage: 'Something went wrong on our end. Our team has been notified and is working on a fix.',
          iconColor: 'text-red-500',
          bgColor: 'bg-red-50'
        };
      case 'empty':
        return {
          icon: DocumentIcon,
          defaultTitle: 'No Data Available',
          defaultMessage: 'There are no items to display at the moment. Try adding some data or adjusting your filters.',
          iconColor: 'text-gray-400',
          bgColor: 'bg-gray-50'
        };
      case 'offline':
        return {
          icon: WifiIcon,
          defaultTitle: 'You\'re Offline',
          defaultMessage: 'Please check your internet connection. Some features may not be available while offline.',
          iconColor: 'text-gray-500',
          bgColor: 'bg-gray-50'
        };
      default:
        return {
          icon: ExclamationTriangleIcon,
          defaultTitle: 'Something Went Wrong',
          defaultMessage: 'An unexpected error occurred. Please try again.',
          iconColor: 'text-amber-500',
          bgColor: 'bg-amber-50'
        };
    }
  };

  const config = getErrorConfig();
  const Icon = config.icon;

  return (
    <div className={`flex flex-col items-center justify-center py-12 px-6 text-center ${className}`}>
      {/* Custom illustration or default icon */}
      {illustration || (
        <div className={`w-20 h-20 ${config.bgColor} rounded-full flex items-center justify-center mb-6`}>
          <Icon className={`w-10 h-10 ${config.iconColor}`} />
        </div>
      )}

      {/* Title */}
      <h3 className="text-lg font-semibold text-gray-900 mb-2">
        {title || config.defaultTitle}
      </h3>

      {/* Message */}
      <p className="text-gray-600 mb-6 max-w-md leading-relaxed">
        {message || config.defaultMessage}
      </p>

      {/* Actions */}
      <div className="flex flex-col sm:flex-row gap-3">
        {showRetry && onRetry && (
          <button
            onClick={onRetry}
            className="inline-flex items-center px-4 py-2 bg-primary-600 text-white font-medium rounded-lg hover:bg-primary-700 transition-colors duration-200"
          >
            <ArrowPathIcon className="w-4 h-4 mr-2" />
            Try Again
          </button>
        )}
        
        {actionLabel && onAction && (
          <button
            onClick={onAction}
            className="inline-flex items-center px-4 py-2 bg-white text-gray-700 font-medium rounded-lg border border-gray-300 hover:bg-gray-50 transition-colors duration-200"
          >
            {actionLabel}
          </button>
        )}
      </div>

      {/* Additional help text for specific error types */}
      {type === 'network' && (
        <div className="mt-6 text-sm text-gray-500">
          <p>Troubleshooting tips:</p>
          <ul className="list-disc list-inside mt-2 space-y-1 text-left">
            <li>Check your internet connection</li>
            <li>Refresh the page</li>
            <li>Try again in a few minutes</li>
          </ul>
        </div>
      )}

      {type === 'server' && (
        <p className="mt-4 text-sm text-gray-500">
          Error ID: {Math.random().toString(36).substring(2, 15)}
        </p>
      )}
    </div>
  );
};

// Specialized error components
export const NetworkError: React.FC<Omit<ErrorStateProps, 'type'>> = (props) => (
  <ErrorState type="network" {...props} />
);

export const NotFoundError: React.FC<Omit<ErrorStateProps, 'type'>> = (props) => (
  <ErrorState type="notFound" {...props} />
);

export const PermissionError: React.FC<Omit<ErrorStateProps, 'type'>> = (props) => (
  <ErrorState type="permission" {...props} />
);

export const ServerError: React.FC<Omit<ErrorStateProps, 'type'>> = (props) => (
  <ErrorState type="server" {...props} />
);

export const EmptyState: React.FC<Omit<ErrorStateProps, 'type'>> = (props) => (
  <ErrorState type="empty" {...props} />
);

export const OfflineError: React.FC<Omit<ErrorStateProps, 'type'>> = (props) => (
  <ErrorState type="offline" {...props} />
);

export default ErrorState;
